import { useState } from 'react';
import { env } from '@/config/env';
import { cn } from '@/lib/utils';

interface CategoryIconProps {
  name: string;
  iconPath: string | null;
  /** An image to show instead of the saved icon, such as the preview of a picked file. */
  previewSrc?: string;
  /** Sets the size, for example h-6 w-6. */
  className?: string;
  letterClassName?: string;
}

/**
 * A category's icon, or its first letter when it has none. The letter is also the fallback for a
 * missing file: rows can outlive files, since uploads stay on one server's disk.
 */
export function CategoryIcon({ name, iconPath, previewSrc, className, letterClassName }: CategoryIconProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = previewSrc ?? (iconPath ? `${env.API_URL}/uploads/${iconPath}` : null);
  const showImage = src !== null && src !== failedSrc;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md',
        // Logos are often dark on transparent, so they get a light tile in both themes.
        showImage ? 'border border-border/60 bg-white' : 'bg-primary/10 text-primary',
        className,
      )}
    >
      {showImage ? (
        <img src={src} alt="" className="h-full w-full object-contain p-0.5" onError={() => setFailedSrc(src)} />
      ) : (
        <span className={cn('text-xs font-semibold uppercase', letterClassName)}>{name.trim().charAt(0)}</span>
      )}
    </span>
  );
}

interface CategoryLabelProps {
  name: string;
  iconPath: string | null;
}

/** Icon and name for a top-aligned table cell, lined up with the row's 32px icon buttons. */
export function CategoryLabel({ name, iconPath }: CategoryLabelProps) {
  return (
    <span className="flex items-start gap-2 py-1">
      <CategoryIcon name={name} iconPath={iconPath} className="h-6 w-6" />
      <span className="min-w-0 py-0.5 font-medium text-foreground [overflow-wrap:anywhere]">{name}</span>
    </span>
  );
}

/** Icon and name for a dropdown option. */
export function CategoryOption({ name, iconPath }: CategoryLabelProps) {
  return (
    <span className="flex items-center gap-2">
      <CategoryIcon name={name} iconPath={iconPath} className="h-5 w-5" letterClassName="text-[10px]" />
      <span className="truncate">{name}</span>
    </span>
  );
}
